import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Edit, 
  Eye, 
  Plus, 
  Filter,
  Calendar,
  User,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  FileText,
  Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cobrancaService } from '../services/api';
import { formatCurrency, formatDate, formatCurrencyAbbreviated } from '../utils/formatters';

const ListaCobrancas = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isGestao = user?.tipo_usuario === 'master' || user?.tipo_usuario === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Estados de filtro
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Buscar cobranças
  const { data: cobrancas = [], isLoading, error, refetch } = useQuery({
    queryKey: ['cobrancas'],
    queryFn: () => cobrancaService.list(),
    staleTime: 300000, // 5 minutos
  });

  // Mutation para deletar cobrança
  const deleteCobrancaMutation = useMutation({
    mutationFn: cobrancaService.delete,
    onSuccess: () => {
      toast.success('Cobrança removida com sucesso!');
      queryClient.invalidateQueries(['cobrancas']);
    },
    onError: (error) => {
      toast.error(`Erro ao remover cobrança: ${error.message}`);
    }
  });

  // Filtrar cobranças
  const filteredCobrancas = cobrancas.filter(cobranca => {
    const matchesSearch = !searchTerm || 
      cobranca.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cobranca.observacoes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || cobranca.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Status options
  const statusOptions = [
    { value: 'all', label: 'Todos os Status', icon: Filter },
    { value: 'nao_cobrado', label: 'Não Cobrado', icon: Clock, color: 'text-gray-600' },
    { value: 'cobrado', label: 'Cobrado', icon: CheckCircle, color: 'text-blue-600' },
    { value: 'promessa_pagamento', label: 'Promessa de Pagamento', icon: Calendar, color: 'text-yellow-600' },
    { value: 'parcialmente_pago', label: 'Parcialmente Pago', icon: DollarSign, color: 'text-orange-600' },
    { value: 'pago', label: 'Pago', icon: CheckCircle, color: 'text-green-600' },
  ];

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    if (!statusOption) return Clock;
    return statusOption.icon;
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    if (!statusOption) return 'text-gray-600';
    return statusOption.color;
  };

  const handleDelete = (cobrancaId) => {
    if (window.confirm('Tem certeza que deseja remover esta cobrança?')) {
      deleteCobrancaMutation.mutate(cobrancaId);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Controle de Cobranças</h1>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Controle de Cobranças</h1>
          <button
            onClick={() => refetch()}
            className="btn-primary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
        <div className="card">
          <div className="text-center text-red-600 dark:text-red-400">
            Erro ao carregar cobranças
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Controle de Cobranças</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Gerencie todas as cobranças registradas
          </p>
        </div>
        <button
          onClick={() => navigate('/controle-cobranca/novo')}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Cobrança
        </button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Busca */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cliente ou observações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Filtro de Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro de Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input w-full"
            >
              <option value="all">Todos os períodos</option>
              <option value="today">Hoje</option>
              <option value="week">Esta semana</option>
              <option value="month">Este mês</option>
              <option value="quarter">Este trimestre</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Cobranças */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cobranças ({filteredCobrancas.length})
          </h2>
          <button
            onClick={() => refetch()}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>

        {filteredCobrancas.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <FileText className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Nenhuma cobrança encontrada com os filtros aplicados'
                : 'Nenhuma cobrança registrada ainda'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/controle-cobranca/novo')}
                className="btn-primary mt-4"
              >
                Registrar Primeira Cobrança
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Responsável
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCobrancas.map((cobranca) => {
                  const StatusIcon = getStatusIcon(cobranca.status);
                  const statusColor = getStatusColor(cobranca.status);
                  
                  return (
                    <tr key={cobranca.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {cobranca.cliente_nome}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {cobranca.observacoes && cobranca.observacoes.length > 50 
                              ? `${cobranca.observacoes.substring(0, 50)}...`
                              : cobranca.observacoes
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <StatusIcon className={`h-4 w-4 mr-2 ${statusColor}`} />
                          <span className={`text-sm font-medium ${statusColor}`}>
                            {cobranca.status_display}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <div>
                          <div>{formatDate(cobranca.data_criacao)}</div>
                          {cobranca.data_promessa_pagamento && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Promessa: {formatDate(cobranca.data_promessa_pagamento)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {cobranca.usuario_nome || cobranca.responsavel_cobranca || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {cobranca.contas_cobranca?.length || 0} contas
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/controle-cobranca/${cobranca.id}`)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/controle-cobranca/${cobranca.id}/editar`)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {isGestao && (
                            <button
                              onClick={() => handleDelete(cobranca.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaCobrancas; 